import React, { useMemo, useState } from 'react';
import { Button, Card, Empty, message, Skeleton, Table } from 'antd';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';

import TransactionSummaryFilterForm from '../../components/form/TransactionSummaryFilterForm';
import { fetchTeacherList } from '../form/store/enrollmentAction';
import tableService from '../../../services/tableService';
import profileService from '../../../services/profileService';
import formService from '../../../services/formService';
import dataTransform from '../../../util/dataTransform';

const text = {
  selectTeacher: '\u8bf7\u5148\u9009\u62e9\u8001\u5e08',
  loadFailed: '\u5de5\u8d44\u7ed3\u7b97\u8bfb\u53d6\u5931\u8d25',
  project: '\u4e0a\u8bfe\u7c7b\u578b',
  amount: '\u5de5\u8d44',
  count: '\u6b21\u6570',
  settlement: '\u7ed3\u7b97',
  total: '\u603b\u8ba1',
  hourlyFee: '\u8bfe\u65f6\u8d39',
  commission: '\u4e1a\u7ee9',
  wage: '\u5de5\u8d44',
  hourlyTotal: '\u8bfe\u65f6\u8d39\u603b\u8ba1',
  performance: '\u4e1a\u7ee9',
  performanceCommission: '\u4e1a\u7ee9\u63d0\u6210',
  performanceTotal: '\u4e1a\u7ee9\u63d0\u6210\u603b\u8ba1',
  studentName: '\u540d\u5b57',
  courseTypeDisplayName: '\u8bfe\u7a0b\u7c7b\u522b\u540d\u79f0',
  actualReceipt: '\u5b9e\u9645\u6536\u6b3e',
  referralCommission: '\u63a8\u8350\u4eba\u63d0\u6210',
  performanceIncome: '\u4e1a\u7ee9\u6536\u5165',
  settle: '\u7ed3\u7b97',
  settleSuccess: '\u7ed3\u7b97\u5df2\u53d1\u9001\u7ed9\u8001\u5e08',
  settleFailed: '\u7ed3\u7b97\u53d1\u9001\u5931\u8d25',
  selectBeforeSettle: '\u8bf7\u5148\u67e5\u8be2\u8001\u5e08\u5de5\u8d44',
  settleMonth: '\u7ed3\u7b97\u5e74\u6708',
};

const statisticYearStorageKey = 'pa_salary_statistic_years';
const statisticRecordStorageKey = 'pa_salary_statistic_records';

const columns = [
  {
    title: text.project,
    dataIndex: 'name',
    key: 'name',
    width: '25%',
  },
  {
    title: text.amount,
    dataIndex: 'amount',
    key: 'amount',
    width: '25%',
  },
  {
    title: text.count,
    dataIndex: 'count',
    key: 'count',
    width: '25%',
  },
  {
    title: text.settlement,
    dataIndex: 'settlement',
    key: 'settlement',
    width: '25%',
  },
];

const commissionColumns = [
  {
    title: text.studentName,
    dataIndex: 'studentName',
    key: 'studentName',
    width: '15%',
  },
  {
    title: text.courseTypeDisplayName,
    dataIndex: 'courseTypeDisplayName',
    key: 'courseTypeDisplayName',
    width: '15%',
  },
  {
    title: text.actualReceipt,
    dataIndex: 'total_aud',
    key: 'total_aud',
    width: '15%',
  },
  {
    title: text.performanceCommission,
    dataIndex: 'performanceCommissionAmount',
    key: 'performanceCommissionAmount',
    width: '15%',
  },
  {
    title: text.referralCommission,
    dataIndex: 'referralCommissionAmount',
    key: 'referralCommissionAmount',
    width: '15%',
  },
  {
    title: text.performanceIncome,
    dataIndex: 'performanceIncome',
    key: 'performanceIncome',
    width: '25%',
  }
];

const wageColumns = [
  {
    title: text.hourlyTotal,
    dataIndex: 'hourlyTotal',
    key: 'hourlyTotal',
  },
  {
    title: text.performanceCommission,
    dataIndex: 'performanceCommissionTotal',
    key: 'performanceCommissionTotal',
  },
  {
    title: text.total,
    dataIndex: 'total',
    key: 'total',
  },
];

const getQueryValue = (query, key) => {
  const params = new URLSearchParams(String(query || '').replace(/^&/, ''));
  return params.get(key);
};

const getFeedbackQuery = (query, teacherId) => {
  let feedbackQuery = String(query || '').replace(/trialTeacher=/g, 'teacher=');
  if (teacherId && !feedbackQuery.includes('teacher=')) {
    feedbackQuery += `&teacher=${teacherId}`;
  }
  return feedbackQuery;
};

const getReferralQuery = (query, teacherId) => {
  const params = new URLSearchParams(String(query || '').replace(/^&/, ''));
  params.delete('trialTeacher');
  params.set('course_advisor', teacherId);
  return `&${params.toString()}`;
};

const money = (value) => Math.round(Number(value || 0) * 100) / 100;
const totalText = (value) => Number(value || 0).toFixed(1);

const getMonthKeyFromQuery = (query) => {
  const from = getQueryValue(query, 'from');
  const to = getQueryValue(query, 'to');
  const dateText = [from, to].find((item) => /^\d{4}[-/]\d{2}/.test(String(item || '')));
  if (dateText) {
    return String(dateText).slice(0, 7).replace('/', '-');
  }
  return new Date().toISOString().slice(0, 7);
};

const getMonthLabel = (monthKey) => {
  const [year, month] = String(monthKey || '').split('-');
  if (!year || !month) {
    return '-';
  }
  return `${year}\u5e74${Number(month)}\u6708`;
};

const ensureStatisticYear = (year) => {
  if (!year) {
    return;
  }
  try {
    const years = JSON.parse(localStorage.getItem(statisticYearStorageKey) || '[]');
    if (!Array.isArray(years) || years.includes(year)) {
      return;
    }
    const nextYears = [year, ...years].sort((a, b) => Number(b) - Number(a));
    localStorage.setItem(statisticYearStorageKey, JSON.stringify(nextYears));
  } catch (err) {
    localStorage.setItem(statisticYearStorageKey, JSON.stringify([year]));
  }
};

const saveStatisticSettlement = (settlement) => {
  try {
    const records = JSON.parse(localStorage.getItem(statisticRecordStorageKey) || '[]');
    const currentRecords = Array.isArray(records) ? records : [];
    const nextRecords = [
      settlement,
      ...currentRecords.filter(
        (item) =>
          !(
            item.teacherId === settlement.teacherId &&
            getMonthKeyFromQuery(`&from=${item.statisticMonth || item.from || ''}`) === settlement.statisticMonth
          )
      ),
    ];
    localStorage.setItem(statisticRecordStorageKey, JSON.stringify(nextRecords));
  } catch (err) {
    localStorage.setItem(statisticRecordStorageKey, JSON.stringify([settlement]));
  }
};

const SalarySettlement = () => {
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });
  const [loading, setLoading] = useState(false);
  const [salaryRows, setSalaryRows] = useState([]);
  const [commissionRows, setCommissionRows] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [currentQuery, setCurrentQuery] = useState('');
  const [settlementMonthKey, setSettlementMonthKey] = useState(
    getMonthKeyFromQuery('')
  );
  const [settling, setSettling] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const dispatch = useDispatch();
  const teacherList = useSelector((state) => state.teacherList);

  const searchSalary = async (current, pageSize, query) => {
    setSettlementMonthKey(getMonthKeyFromQuery(query));
    const teacherId = getQueryValue(query, 'trialTeacher');
    if (!teacherId) {
      setSalaryRows([]);
      setCommissionRows([]);
      setSelectedTeacher(null);
      setCurrentQuery('');
      setHasSearched(false);
      message.info(text.selectTeacher);
      return;
    }

    setLoading(true);
    try {
      const [teacherRes, courseTypeRes, feedbackRes, transactionRes, referralRes] = await Promise.all([
        profileService.getUser(teacherId),
        formService.queryCourseType(true),
        tableService.getFeedbackDetails(1, 10000, getFeedbackQuery(query, teacherId)),
        tableService.getTransactionSummary(1, 10000, query),
        tableService.getTransactionSummary(1, 10000, getReferralQuery(query, teacherId)),
      ]);
      setSelectedTeacher(teacherRes.data || null);
      setCurrentQuery(query);
      const courseCommissions = (teacherRes.data && teacherRes.data.courseCommissions) || {};
      const currentTeacherName = teacherRes.data && (teacherRes.data.cnname || teacherRes.data.name);
      const currentCommissionRate = Number(courseCommissions.courseCommission || 0);
      const currentReferralRate = Number(courseCommissions.referralCommission || 10);
      const courseTypes = courseTypeRes.data || [];
      const feedbacks = (feedbackRes.data && feedbackRes.data.item) || [];
      const countMap = feedbacks.reduce((result, feedback) => {
        result[feedback.courseType] = (result[feedback.courseType] || 0) + 1;
        return result;
      }, {});
      const rows = courseTypes
        .filter((courseType) => {
          const value = courseCommissions[courseType.type];
          return value !== undefined && value !== null && value !== '';
        })
        .map((courseType) => ({
          key: courseType.type,
          name: courseType.name,
          amount: courseCommissions[courseType.type],
          count: countMap[courseType.type] || 0,
        }))
        .filter((row) => Number(row.count || 0) > 0)
        .map((row) => ({
          ...row,
          settlement: Number(row.amount || 0) * Number(row.count || 0),
        }));
      setSalaryRows(rows);
      const { displayData } = dataTransform.transactionSummaryDataTransform(
        transactionRes.data || { total: 0, totalTransaction: 0, item: [] }
      );
      const { displayData: referralDisplayData } = dataTransform.transactionSummaryDataTransform(
        referralRes.data || { total: 0, totalTransaction: 0, item: [] }
      );
      const rowMap = [...displayData, ...referralDisplayData].reduce((result, row) => {
        if (!result[row.key]) {
          result[row.key] = row;
        }
        return result;
      }, {});
      const commissionRows = Object.values(rowMap).map((row) => {
        const totalAud = Number(row.total_aud || 0);
        const isReferral = row.advisor === currentTeacherName;
        const isPerformance = row.trialTeacher === currentTeacherName && !isReferral;
        const performanceCommissionAmount = isPerformance
          ? money((totalAud * currentCommissionRate) / 100)
          : 0;
        const referralCommissionAmount = isReferral
          ? Math.min(200, money((totalAud * currentReferralRate) / 100))
          : 0;
        return {
          ...row,
          performanceCommissionAmount,
          referralCommissionAmount,
          performanceIncome: money(performanceCommissionAmount + referralCommissionAmount),
        };
      });
      setCommissionRows(commissionRows);
      setPagination({ current, pageSize });
      setHasSearched(true);
    } catch (err) {
      message.error(text.loadFailed);
    } finally {
      setLoading(false);
    }
  };

  const filterCbs = useMemo(() => {
    return bindActionCreators(
      {
        fetchTeacherList,
      },
      dispatch
    );
  }, []);

  const hourlyTotal = salaryRows.reduce(
    (sum, row) => sum + Number(row.settlement || 0),
    0
  );
  const performanceCommissionAmount = money(
    commissionRows.reduce((sum, row) => sum + Number(row.performanceIncome || 0), 0)
  );
  const wageRows = [
    {
      key: 'wage',
      hourlyTotal: totalText(hourlyTotal),
      performanceCommissionTotal: totalText(performanceCommissionAmount),
      total: totalText(hourlyTotal + performanceCommissionAmount),
    },
  ];
  const settlementMonthLabel = getMonthLabel(settlementMonthKey);

  const handleSettle = async () => {
    if (!hasSearched || !selectedTeacher) {
      message.info(text.selectBeforeSettle);
      return;
    }
    const params = new URLSearchParams(String(currentQuery || '').replace(/^&/, ''));
    const targetMonthKey = getMonthKeyFromQuery(currentQuery);
    const settlement = {
      key: `${Date.now()}`,
      createdAt: new Date().toISOString(),
      teacherId: selectedTeacher._id,
      teacherName: selectedTeacher.cnname || selectedTeacher.name,
      from: params.get('from') || '',
      to: params.get('to') || '',
      statisticMonth: targetMonthKey,
      salaryRows,
      commissionRows,
      wageRows,
    };
    setSettling(true);
    try {
      const statisticSettlements = selectedTeacher.salaryStatisticSettlements || selectedTeacher.salarySettlements || [];
      const nextStatisticSettlements = [
        settlement,
        ...statisticSettlements.filter(
          (item) => getMonthKeyFromQuery(`&from=${item.statisticMonth || item.from || ''}`) !== targetMonthKey
        ),
      ];
      await profileService.updateUser(selectedTeacher._id, {
        salarySettlements: [settlement],
        salaryStatisticSettlements: nextStatisticSettlements,
      });
      setSelectedTeacher({
        ...selectedTeacher,
        salarySettlements: [settlement],
        salaryStatisticSettlements: nextStatisticSettlements,
      });
      ensureStatisticYear(String(targetMonthKey).slice(0, 4));
      saveStatisticSettlement(settlement);
      message.success(text.settleSuccess);
    } catch (err) {
      message.error(text.settleFailed);
    } finally {
      setSettling(false);
    }
  };

  return (
    <div>
      <Card style={{ marginTop: 10 }}>
        <TransactionSummaryFilterForm
          {...filterCbs}
          search={searchSalary}
          teacherList={teacherList}
          pagination={pagination}
          showStudent={false}
          showAdvisor={false}
          onQueryChange={(query) => setSettlementMonthKey(getMonthKeyFromQuery(query))}
        />
      </Card>
      <Card title={text.hourlyFee} style={{ marginTop: 10 }}>
        {loading ? (
          <Skeleton active />
        ) : salaryRows.length ? (
          <Table
            dataSource={salaryRows}
            columns={columns}
            pagination={false}
            summary={(pageData) => {
              const total = pageData.reduce(
                (sum, row) => sum + Number(row.settlement || 0),
                0
              );
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0}>{text.total}</Table.Summary.Cell>
                  <Table.Summary.Cell index={1} />
                  <Table.Summary.Cell index={2} />
                  <Table.Summary.Cell index={3}>{totalText(total)}</Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card title={text.commission} style={{ marginTop: 10 }}>
        {loading ? (
          <Skeleton active />
        ) : commissionRows.length ? (
          <Table
            dataSource={commissionRows}
            columns={commissionColumns}
            pagination={false}
            tableLayout='fixed'
            summary={(pageData) => {
              const total = pageData.reduce(
                (sum, row) => sum + Number(row.total_aud || 0),
                0
              );
              const performanceTotal =
                pageData.reduce((sum, row) => sum + Number(row.performanceCommissionAmount || 0), 0);
              const referralTotal =
                pageData.reduce((sum, row) => sum + Number(row.referralCommissionAmount || 0), 0);
              const performanceIncomeTotal =
                pageData.reduce((sum, row) => sum + Number(row.performanceIncome || 0), 0);
              return (
                <>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0}>{text.total}</Table.Summary.Cell>
                    <Table.Summary.Cell index={1} />
                    <Table.Summary.Cell index={2}>{totalText(total)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={3}>{totalText(performanceTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>{totalText(referralTotal)}</Table.Summary.Cell>
                    <Table.Summary.Cell index={5}>{totalText(performanceIncomeTotal)}</Table.Summary.Cell>
                  </Table.Summary.Row>
                </>
              );
            }}
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card title={text.wage} style={{ marginTop: 10 }}>
        {loading ? (
          <Skeleton active />
        ) : hasSearched ? (
          <Table
            dataSource={wageRows}
            columns={wageColumns}
            pagination={false}
          />
        ) : (
          <Empty />
        )}
      </Card>
      <div style={{ textAlign: 'left', marginTop: 16, marginBottom: 30 }}>
        <div style={{ marginBottom: 8, fontWeight: 500 }}>
          {text.settleMonth}: {settlementMonthLabel}
        </div>
        <Button
          type='primary'
          loading={settling}
          disabled={!hasSearched || loading}
          onClick={handleSettle}
        >
          {text.settle}
        </Button>
      </div>
    </div>
  );
};

export default SalarySettlement;
